import React, { useState, useEffect } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SchoolIcon from "@mui/icons-material/School";
import CircularProgress from "@mui/material/CircularProgress";
import { postRightworkAPI } from "../requests/utilities/requests";
import { API_ops_assistant_confirm_action } from "../../../constants";
import { toast } from "react-toastify";

export default function TrainingCheckpointProposalCard({
  proposal,
  sessionUuid,
  homeStore,
  onStatusChange,
}) {
  const [status, setStatus] = useState(proposal.status || "pending");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (
      (proposal.status === "cancelled" || proposal.status === "rejected") &&
      status === "pending"
    ) {
      setStatus("cancelled");
    }
  }, [proposal.status]);

  const summary = proposal.summary || {};
  const isQuiz = summary.block_type === "quiz";

  const handleAction = async (action) => {
    setLoading(true);
    try {
      const payload = {
        store_id: homeStore,
        session_uuid: sessionUuid,
        proposal_id: proposal.proposal_id,
        action: action,
      };
      const res = await postRightworkAPI(
        API_ops_assistant_confirm_action,
        payload
      );
      if (res.status === 200) {
        const newStatus = action === "confirm" ? "confirmed" : "cancelled";
        setStatus(newStatus);
        onStatusChange?.(proposal.proposal_id, newStatus);
      } else {
        toast.error("Failed to process action.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.system?.message || err?.response?.data?.error || "Failed to process action.");
    }
    setLoading(false);
  };

  // Confirmed state
  if (status === "confirmed") {
    return (
      <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700 my-2">
        <CheckCircleIcon fontSize="small" className="text-green-600 mt-0.5 shrink-0" />
        <div className="min-w-0">
          <div className="font-medium">{isQuiz ? "Quiz override completed" : "Checkpoint completed"}</div>
          {summary.employee_name && (
            <div className="text-xs opacity-80">{summary.employee_name}</div>
          )}
        </div>
      </div>
    );
  }

  // Cancelled state
  if (status === "cancelled" || status === "rejected") {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm text-gray-500 my-2">
        <CancelIcon fontSize="small" />
        <span>{isQuiz ? "Quiz override cancelled" : "Checkpoint cancelled"}</span>
      </div>
    );
  }

  // Pending state
  return (
    <div className="my-2 rounded-lg border border-violet-200 bg-violet-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-violet-100 border-b border-violet-200">
        <SchoolIcon fontSize="small" className="text-violet-700" />
        <span className="text-sm font-medium text-violet-800">
          {summary.title || (isQuiz ? "Override Failed Quiz" : "Complete Checkpoint")}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2 space-y-1">
        {summary.employee_name && (
          <div className="text-sm">
            <span className="text-gray-500">Employee: </span>
            <span className="font-medium text-gray-700">
              {summary.employee_name}
            </span>
          </div>
        )}
        {summary.track_name && (
          <div className="text-sm">
            <span className="text-gray-500">Track: </span>
            <span className="font-medium text-gray-700">
              {summary.track_name}
            </span>
          </div>
        )}
        {summary.section_name && (
          <div className="text-sm">
            <span className="text-gray-500">Section: </span>
            <span className="font-medium text-gray-700">
              {summary.section_name}
            </span>
          </div>
        )}
        {summary.block_name && (
          <div className="text-sm">
            <span className="text-gray-500">{isQuiz ? "Quiz: " : "Checkpoint: "}</span>
            <span className="font-medium text-gray-700">
              {summary.block_name}
            </span>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-end gap-2 px-3 py-2 border-t border-violet-200 bg-white">
        <button
          onClick={() => handleAction("reject")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={() => handleAction("confirm")}
          disabled={loading}
          className="px-3 py-1.5 text-sm rounded-md bg-violet-700 text-white hover:bg-violet-800 disabled:opacity-50 flex items-center gap-1"
        >
          {loading && <CircularProgress size={14} sx={{ color: "white" }} />}
          {isQuiz ? "Override" : "Complete"}
        </button>
      </div>
    </div>
  );
}
